import { useFilters, useAlert } from "@wit/mpesa-ui-components";
import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { IStoreInterface } from "../../../configs/store.config";
import { filterServiceManagerServices } from "../service-manager.utils";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import ServiceManagerFilters from "../components/service-manager-filters.component";
import ServiceManagerListItem from "./service-list-item.component";
import { ServiceManagerActions } from "../service-manager.store";
import ServiceManagerApi from "../service-manager.api";
import { AlertTypeEnum } from "@wit/mpesa-ui-components/lib/context/alert/alert.context";
import LoadingComponent from "../../../shared/components/loading-component/LoadingComponent";
import { SelectedView } from "../../../shared/shared.enums";
import AddServiceManagerItem from "../components/add-service-manager-item.component";
import { FEATURES } from "../../../shared/renderer.utils";
import useCheckFeatureAvailable from "../../../shared/hooks/use-check-available-feature";
import { ConfigContext } from "../../../app.component";
import { IServiceGroup } from "../../../shared/models/service-manager.model";

/**
 * Page Service List
 */
const ServiceManagerList = () => {
  const {
    filters,
    updateFilter,
    isFilterActive,
    clearFilter,
    resetFilters,
    getFilterValue,
    getFiltersQueryString,
    updateMultipleFilters,
  } = useFilters();

  const [t] = useTranslation();
  const dispatch = useDispatch();
  const [selectedView, setSelectedView] = React.useState<SelectedView>(SelectedView.GRID);
  const { services, isLoadingServices } = useSelector((state: IStoreInterface) => state.serviceManagerReducer);
  const [showAlert, hideAlert, setAlertProps] = useAlert();
  const [showableServices, setShowableServices] = React.useState(services);
  const addAndRemoveServicesEnabled = useCheckFeatureAvailable(FEATURES.ADD_AND_REMOVE_SERVICES);
  const { config } = React.useContext(ConfigContext);
  const serviceGroups: IServiceGroup[] = config?.serviceGroups || [];

  React.useEffect(() => {
    dispatch(ServiceManagerActions.creators.fetchingServicesAction());
    ServiceManagerApi.methods.getServices().then(
      res => {
        dispatch(ServiceManagerActions.creators.fetchServicesSuccessAction(res.data));
      },
      () => {
        setAlertProps({
          title: t("pages.serviceBuilder.errors.searchServices"),
          type: AlertTypeEnum.ERROR,
        });
        showAlert();
      },
    );
  }, [dispatch, t, showAlert, setAlertProps]);

  React.useEffect(() => {
    setShowableServices(services);
  }, [services]);

  React.useEffect(() => {
    if (filters.size > 0) {
      setShowableServices(filterServiceManagerServices(services, filters, serviceGroups));
    } else {
      setShowableServices(services);
    }
  }, [filters, services]);

  function showNoResults(): boolean {
    if (showableServices.length === 0) {
      return !(
        filters.size === 0 ||
        (filters.size === 1 && filters.get("search") !== undefined && (filters.get("search") as string[])[0] === "")
      );
    } else {
      return false;
    }
  }

  function renderServices() {
    return (
      <ServiceItemsContainer>
        {addAndRemoveServicesEnabled && <AddServiceManagerItem></AddServiceManagerItem>}
        {showableServices.map(service => (
          <ServiceManagerListItem key={service.id} item={service} />
        ))}
      </ServiceItemsContainer>
    );
  }

  return (
    <>
      {isLoadingServices ? (
        <LoadingContainer>
          <div>
            <LoadingComponent />
          </div>
        </LoadingContainer>
      ) : (
        <>
          <ServiceManagerFilters
            filters={filters}
            isFilterActive={isFilterActive}
            updateFilter={updateFilter}
            clearFilter={clearFilter}
            resetFilters={resetFilters}
            getFilterValue={getFilterValue}
            getFiltersQueryString={getFiltersQueryString}
            updateMultipleFilters={updateMultipleFilters}
            selectedView={selectedView}
            setSelectedView={setSelectedView}
          />
          {showNoResults() ? (
            <MagnifierContainer>
              <img src={"./images/ilustration-search.svg"} alt="search-icon" />
              <NoResultsFoundText>{t("pages.serviceBuilder.errors.noSearchResults")}</NoResultsFoundText>
            </MagnifierContainer>
          ) : (
            renderServices()
          )}
        </>
      )}
    </>
  );
};

export default ServiceManagerList;

const ServiceItemsContainer = styled("div")`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(max(350px, (100% - (5 - 1) * 10px)/5), 1fr));
  column-gap: 24px;
  row-gap: 24px;
  flex: 1;
  margin-bottom: 45px;
`;

const MagnifierContainer = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  margin-top: 50px;

  svg {
    width: 140px;
    height: 140px;
    margin-bottom: 24px;
    stroke: red;
  }
`;

const NoResultsFoundText = styled("span")`
  font-family: Vodafone Rg;
  font-size: 22px;
  color: ${props => props.theme.palette.midGrey};
`;

const LoadingContainer = styled("div")`
  height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;
